import React, { useState, useEffect, useRef } from "react";
import TabNavigation from "./TabNavigation";
import TabContent from "./TabContent";
import OptionSelector from "./shared/OptionSelector";
import {
  getWindowConfig,
  getVisibleTabs,
  getFieldsToClear,
  shouldRenderHiddenField,
} from "./config/windowTypeConfigs";
import { MEASUREMENT_FIELDS, MEASUREMENT_TABS } from "./constants/fieldNames";

const WindowOptions = ({ windowProduct, windowItem, availableOptions }) => {
  const [formData, setFormData] = useState(windowItem || {});
  const [activeTab, setActiveTab] = useState(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Set initial active tab based on window config
  useEffect(() => {
    const config = getWindowConfig(windowProduct.name);
    setActiveTab(config.tabs[0]);
  }, [windowProduct.name]);

  // Clear invalid option IDs when window product changes
  useEffect(() => {
    setFormData((prev) => {
      const newData = { ...prev };

      // Clear option IDs that don't belong to the new product
      const validGridLocationIds = availableOptions.grid_locations.map(
        (opt) => opt.id
      );
      const validGridStyleIds = availableOptions.grid_styles.map(
        (opt) => opt.id
      );
      const validObscureGlassIds = availableOptions.obscure_glass_options.map(
        (opt) => opt.id
      );
      const validInternalBlindIds = availableOptions.internal_blinds.map(
        (opt) => opt.id
      );
      const validWindowRemovalIds = availableOptions.window_removals.map(
        (opt) => opt.id
      );
      const validSizeIds = availableOptions.window_product_sizes.map(
        (opt) => opt.id
      );
      const validEndLiteStyleIds = availableOptions.end_lite_styles.map(
        (opt) => opt.id
      );
      const validEndLiteGridLocationIds =
        availableOptions.end_lite_grid_locations.map((opt) => opt.id);
      const validPanelRatioIds = (availableOptions.panel_ratios || []).map(
        (opt) => opt.id
      );

      if (
        newData.grid_location_id &&
        !validGridLocationIds.includes(newData.grid_location_id)
      ) {
        newData.grid_location_id = "";
      }
      if (
        newData.grid_style_id &&
        !validGridStyleIds.includes(newData.grid_style_id)
      ) {
        newData.grid_style_id = "";
      }
      if (
        newData.obscure_glass_option_id &&
        !validObscureGlassIds.includes(newData.obscure_glass_option_id)
      ) {
        newData.obscure_glass_option_id = "";
      }
      if (
        newData.internal_blind_id &&
        !validInternalBlindIds.includes(newData.internal_blind_id)
      ) {
        newData.internal_blind_id = "";
      }
      if (
        newData.window_removal_id &&
        !validWindowRemovalIds.includes(newData.window_removal_id)
      ) {
        newData.window_removal_id = "";
      }
      if (
        newData.window_product_size_id &&
        !validSizeIds.includes(newData.window_product_size_id)
      ) {
        newData.window_product_size_id = "";
      }
      if (
        newData.end_lite_style_id &&
        !validEndLiteStyleIds.includes(newData.end_lite_style_id)
      ) {
        newData.end_lite_style_id = "";
      }
      if (
        newData.end_lite_grid_location_id &&
        !validEndLiteGridLocationIds.includes(newData.end_lite_grid_location_id)
      ) {
        newData.end_lite_grid_location_id = "";
      }
      if (
        newData.panel_ratio_id &&
        !validPanelRatioIds.includes(newData.panel_ratio_id)
      ) {
        newData.panel_ratio_id = "";
      }

      return newData;
    });
  }, [windowProduct.id, availableOptions]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      const fieldsToClear = getFieldsToClear(
        windowProduct.name,
        field,
        value,
        prev,
        availableOptions
      );

      fieldsToClear.forEach((fieldToClear) => {
        newData[fieldToClear] = "";
      });

      return newData;
    });
  };

  // Update file input when job_photos changes
  useEffect(() => {
    if (
      fileInputRef.current &&
      formData.job_photos &&
      formData.job_photos.length > 0
    ) {
      const dataTransfer = new DataTransfer();
      formData.job_photos.forEach((file) => {
        if (file instanceof File) {
          dataTransfer.items.add(file);
        }
      });
      fileInputRef.current.files = dataTransfer.files;
    }
  }, [formData.job_photos]);

  const config = getWindowConfig(windowProduct.name);

  const visibleTabs = getVisibleTabs(
    windowProduct.name,
    formData,
    availableOptions
  );

  // Switch active tab if current tab is no longer visible
  useEffect(() => {
    if (activeTab && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);

  // Handle invalid event on measurement inputs - switch to tab if validation fails
  useEffect(() => {
    if (!containerRef.current) return;

    const handleInvalid = (event) => {
      const invalidInput = event.target;
      const inputName = invalidInput.name || invalidInput.id;

      // Check if the invalid field is a measurement field
      if (MEASUREMENT_FIELDS.includes(inputName)) {
        // Switch to measurements or sizes tab
        const targetTab = visibleTabs.find((tab) =>
          MEASUREMENT_TABS.includes(tab)
        );
        if (targetTab && activeTab !== targetTab) {
          // Switch to the measurements/sizes tab so user can see the error
          setActiveTab(targetTab);
        }
      }
    };

    // Listen for invalid events on inputs within the component
    const container = containerRef.current;
    container.addEventListener("invalid", handleInvalid, true);

    return () => {
      container.removeEventListener("invalid", handleInvalid, true);
    };
  }, [activeTab, visibleTabs]);

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="flex gap-4 pb-4">
        <OptionSelector
          label="Color"
          name="sales_window_item[color]"
          options={availableOptions.colors.map((color) => ({
            id: color,
            name: color,
          }))}
          value={formData.color}
          onChange={(value) => handleChange("color", value)}
          includeNone={false}
          required
          isStringValue
        />

        {windowProduct.name === "Architectural" && (
          <OptionSelector
            label="Style"
            name="sales_window_item[architectural_style]"
            options={availableOptions.architectural_styles.map((style) => ({
              id: style,
              name: style,
            }))}
            value={formData.architectural_style}
            onChange={(value) => handleChange("architectural_style", value)}
            includeNone={false}
            required
            isStringValue
          />
        )}

        {windowProduct.name === "Bay" &&
          availableOptions.end_lite_styles.length > 0 && (
            <OptionSelector
              label="End Lite Style"
              name="sales_window_item[end_lite_style_id]"
              options={availableOptions.end_lite_styles}
              value={formData.end_lite_style_id}
              onChange={(value) => handleChange("end_lite_style_id", value)}
              includeNone={true}
              noneLabel="Select Style"
              required={true}
            />
          )}

        {config.hasRemoval && availableOptions.window_removals.length > 0 && (
          <OptionSelector
            label="Window Removal"
            name="sales_window_item[window_removal_id]"
            options={availableOptions.window_removals}
            value={formData.window_removal_id}
            onChange={(value) => handleChange("window_removal_id", value)}
            required={true}
          />
        )}

        {availableOptions.panel_ratios?.length > 0 && (
          <OptionSelector
            label="Panel Ratio"
            name="sales_window_item[panel_ratio_id]"
            options={availableOptions.panel_ratios}
            value={formData.panel_ratio_id}
            onChange={(value) => handleChange("panel_ratio_id", value)}
            includeNone={false}
            required={true}
          />
        )}
      </div>

      <div className="p-2 bg-lighter rounded">
        <TabNavigation
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <TabContent
          activeTab={activeTab}
          formData={formData}
          onChange={handleChange}
          availableOptions={availableOptions}
          windowProduct={windowProduct}
          visibleTabs={visibleTabs}
        />
      </div>

      {/* Hidden file input for job photos */}
      <input
        ref={fileInputRef}
        type="file"
        name="sales_window_item[job_photos][]"
        multiple
        style={{ display: "none" }}
      />

      {/* Hidden inputs for photos to remove */}
      {formData.job_photos_to_remove &&
        formData.job_photos_to_remove.map((photoId, index) => (
          <input
            key={`photo-to-remove-${index}`}
            type="hidden"
            name="sales_window_item[job_photos_to_remove][]"
            value={photoId}
          />
        ))}

      {Object.entries(formData).map(([key, value]) => {
        if (value === null || value === undefined) return null;
        // Skip job_photos and job_photos_to_remove - handled separately above
        if (
          key === "job_photos" ||
          key === "job_photos_to_remove" ||
          key === "existing_job_photos"
        )
          return null;
        // Only render hidden fields if they belong to a visible tab or are always-visible fields
        if (!shouldRenderHiddenField(key, visibleTabs)) return null;
        return (
          <input
            key={key}
            type="hidden"
            name={`sales_window_item[${key}]`}
            value={value}
          />
        );
      })}
    </div>
  );
};

export default WindowOptions;
